/**
 * IR -> chart.json bytes and Raw-panel fragment text (W1-07a formats-json-rpe; contract formats/rpe/json.ts
 * `EncodeRpeFn`, `EncodeRpeFragmentFn`; rpe-format.md F§15 writer checklist, DESIGN.md §7.3).
 *
 * Output: UTF-8 without BOM, first byte '{', contains "META"; root keys BPMList, META, judgeLineGroup (when it has
 * names), judgeLineList, then the other RPE root keys and the root passthrough; inside every object the known keys
 * in RPE order followed by its passthrough keys in source order (a passthrough key never overrides a known one).
 * RPE_SCHEMA drives both: `writeObject` walks an object's schema table in order and writes each scalar with the
 * writer method of its schema kind (i32 / u8 / u16 / u32 as integers, f32 as the shortest decimal that reads back
 * as the same f32, f64, bool, string, beat triple, colour, bezier4); only structured members ('json': lists,
 * nested objects, RPEVersion, anchor, control curves) have their own writers. -0 is written as 0, RPEVersion
 * lexemes verbatim (number and string tokens exactly as imported, escapes included).
 *
 * EncodeError (a bug: the export self-check reports it, a broken file is never written) for:
 *  - bezier != 0 inside speedEvents (Phira's 1.7 speed integrator panics on it);
 *  - gifEvents on a line whose textureKind is not 'gif' (Phira's GIF decoder would fail the whole chart), and
 *    ignored lists other than text / paint on textured lines and gif on line.png, or whose JSON Phira's event
 *    parser would refuse (Phira parses those lists although it does not draw them): not a list of event objects,
 *    a beat that is not three integers [i32, u32, u32 > 0], a fractional or out-of-range integer field
 *    (easingType, bezier, linkgroup), a start / end of the wrong type, non-numeric floats;
 *  - an empty event list other than `gifEvents: []` (crashes or disables layers in Phira);
 *  - events that are not positive-length, sorted and non-overlapping, invalid beat triples, an empty BPM list,
 *    note types other than 1-4, and every typing violation the writer refuses (non-finite numbers, fractional or
 *    out-of-range integers).
 */

import type {
  IrBpm,
  IrChart,
  IrControlPoint,
  IrEvent,
  IrExtended,
  IrLayer,
  IrLine,
  IrMeta,
  IrNote,
} from '../../core/ir/types';
import { Beats } from '../../core/time/beat';
import type { Beat, Triple } from '../../core/time/types';
import type { Json, Passthrough, RGB } from '../../core/util/types';
import { parseJsonLossless } from '../json/lexer';
import { EncodeError } from '../json/types';
import type { JsonWriter } from '../json/types';
import { createTextJsonWriter } from '../json/writer';
import type { TextJsonWriter } from '../json/writer';
import type { EncodeRpeFn, EncodeRpeFragmentFn, EncodeStyle, RpeFragmentIr } from './json';
import {
  CONTROL_CURVES,
  EXTENDED_LIST_KEYS,
  fieldSpec,
  isEventListKey,
  isKnownKey,
  knownKeys,
  LAYER_LIST_KEYS,
} from './schema';
import type { ControlCurveKey, EventListKey, RpeObjectKind } from './schema';

type ValueKind = 'f32' | 'rgb' | 'string';

function fail(path: string, message: string): never {
  throw new EncodeError(path === '' ? '(root)' : path, message);
}

/** Passthrough keys in source order; a key the schema knows is never written from here. */
function writeExtra(w: JsonWriter, extra: Passthrough, kind: RpeObjectKind): void {
  // Duplicate passthrough keys: the last occurrence wins (as JSON.parse) and is written at its own position.
  const lastIndex = new Map<string, number>();
  extra.forEach(([key], i) => lastIndex.set(key, i));
  extra.forEach(([key, value], i) => {
    if (lastIndex.get(key) !== i || isKnownKey(kind, key)) return;
    w.key(key);
    w.json(value);
  });
}

/**
 * A known member: a scalar typed by its schema kind, or a writer for a structured ('json') member. Undefined
 * leaves the key out.
 */
type FieldValue = number | string | boolean | readonly number[] | (() => void) | undefined;

/** One scalar written with the writer method of its schema kind. */
function writeField(
  w: JsonWriter,
  kind: RpeObjectKind,
  key: string,
  v: Exclude<FieldValue, undefined>,
  path: string,
): void {
  if (typeof v === 'function') {
    v();
    return;
  }
  const spec = fieldSpec(kind, key);
  const p = path === '' ? key : `${path}.${key}`;
  const num = (): number => (typeof v === 'number' ? v : fail(p, `${key} must be a number`));
  const list = (): readonly number[] =>
    Array.isArray(v) ? (v as readonly number[]) : fail(p, `${key} must be a list`);
  switch (spec?.kind) {
    case 'i32':
    case 'u8':
    case 'u16':
    case 'u32':
      w.int(num(), spec.kind);
      return;
    case 'f32':
      w.f32(num());
      return;
    case 'f64':
      w.f64(num());
      return;
    case 'bool':
      if (typeof v !== 'boolean') fail(p, `${key} must be true or false`);
      w.bool(v);
      return;
    case 'string':
      if (typeof v !== 'string') fail(p, `${key} must be a string`);
      w.string(v);
      return;
    case 'triple':
      writeTriple(w, list() as Triple, p);
      return;
    case 'rgb': {
      const c = list();
      if (c.length !== 3) fail(p, 'a colour must be [r, g, b]');
      writeRgb(w, c as RGB);
      return;
    }
    case 'bezier4': {
      const b = list();
      if (b.length !== 4) fail(p, 'bezierPoints must be four numbers');
      w.beginArray();
      for (const x of b) w.f32(x);
      w.endArray();
      return;
    }
    case 'json':
      fail(p, `${key} is a structured member and needs its own writer`);
      break;
    case undefined:
      throw new Error(`RPE_SCHEMA has no ${kind}.${key}`);
  }
}

/**
 * An object of a schema kind: its known keys in RPE_SCHEMA order (each present one typed by the schema, see
 * writeField), then its passthrough. Every key of `fields` must be in the schema (a writer bug otherwise).
 */
function writeObject(
  w: JsonWriter,
  kind: RpeObjectKind,
  path: string,
  fields: Readonly<Record<string, FieldValue>>,
  extra: Passthrough,
): void {
  for (const key of Object.keys(fields)) {
    if (!isKnownKey(kind, key)) throw new Error(`RPE_SCHEMA has no ${kind}.${key}`);
  }
  w.beginObject();
  for (const key of knownKeys(kind)) {
    const v = fields[key];
    if (v === undefined) continue;
    w.key(key);
    writeField(w, kind, key, v, path);
  }
  writeExtra(w, extra, kind);
  w.endObject();
}

function writeTriple(w: JsonWriter, t: Triple, path: string): void {
  if ((t as readonly number[]).length !== 3) fail(path, 'a beat must be [a, b, c]');
  if (t[2] === 0) fail(path, 'a beat denominator must not be 0');
  w.beginArray();
  w.int(t[0], 'i32');
  w.int(t[1], 'u32');
  w.int(t[2], 'u32');
  w.endArray();
}

function beatOf(t: Triple, path: string): Beat {
  const b = Beats.tryFromTriple(t);
  if (b === null) fail(path, `invalid beat [${t.map(String).join(', ')}]`);
  return b.beat;
}

function writeRgb(w: JsonWriter, c: RGB): void {
  w.beginArray();
  w.int(c[0], 'u8');
  w.int(c[1], 'u8');
  w.int(c[2], 'u8');
  w.endArray();
}

function writeValue(w: JsonWriter, v: unknown, kind: ValueKind, path: string): void {
  if (kind === 'string') {
    if (typeof v !== 'string') fail(path, 'a text event value must be a string');
    w.string(v);
  } else if (kind === 'rgb') {
    if (!Array.isArray(v) || v.length !== 3) fail(path, 'a colour event value must be [r, g, b]');
    writeRgb(w, v as unknown as RGB);
  } else {
    if (typeof v !== 'number') fail(path, 'an event value must be a number');
    w.f32(v);
  }
}

function writeEvent(w: JsonWriter, e: IrEvent<unknown>, kind: ValueKind, path: string, speed: boolean): void {
  if (speed && e.bezier !== 0) {
    fail(`${path}.bezier`, 'speed events never carry a bezier curve (Phira 1.7 speed would crash)');
  }
  writeObject(
    w,
    'event',
    path,
    {
      bezier: e.bezier,
      bezierPoints: e.bezierPoints,
      easingLeft: e.easingLeft,
      easingRight: e.easingRight,
      easingType: e.easingType,
      end: () => {
        writeValue(w, e.end, kind, `${path}.end`);
      },
      endTime: e.endTime,
      linkgroup: e.linkgroup,
      start: () => {
        writeValue(w, e.start, kind, `${path}.start`);
      },
      startTime: e.startTime,
    },
    e.extra,
  );
}

function valueKindOf(list: EventListKey | null, first: IrEvent<unknown> | undefined): ValueKind {
  if (list === 'colorEvents') return 'rgb';
  if (list === 'textEvents') return 'string';
  if (list !== null) return 'f32';
  const v = first?.start;
  return typeof v === 'string' ? 'string' : Array.isArray(v) ? 'rgb' : 'f32';
}

function writeEventList(
  w: JsonWriter,
  list: readonly IrEvent<unknown>[],
  key: EventListKey | null,
  path: string,
): void {
  if (list.length === 0 && key !== 'gifEvents') fail(path, 'an empty event list crashes or disables layers in Phira');
  let prevEnd: Beat | null = null;
  list.forEach((e, i) => {
    const p = `${path}[${String(i)}]`;
    const s = beatOf(e.startTime, `${p}.startTime`);
    const end = beatOf(e.endTime, `${p}.endTime`);
    if (!Beats.lt(s, end)) fail(p, 'an event must have a positive length');
    if (prevEnd !== null && Beats.lt(s, prevEnd)) fail(p, 'events must be sorted and must not overlap');
    prevEnd = end;
  });
  const kind = valueKindOf(key, list[0]);
  w.beginArray();
  list.forEach((e, i) => {
    writeEvent(w, e, kind, `${path}[${String(i)}]`, key === 'speedEvents');
  });
  w.endArray();
}

function writeLayer(w: JsonWriter, layer: IrLayer, path: string): void {
  const fields: Record<string, FieldValue> = {};
  for (const key of LAYER_LIST_KEYS) {
    const list = layer[key];
    if (list !== undefined) {
      fields[key] = () => {
        writeEventList(w, list, key, `${path}.${key}`);
      };
    } else if (layer.nullKeys.includes(key)) {
      fields[key] = () => {
        w.null();
      };
    }
  }
  writeObject(w, 'layer', path, fields, layer.extra);
}

const JSON_INT_RANGES = {
  i32: [-2147483648, 2147483647],
  u8: [0, 255],
  u32: [0, 4294967295],
} as const;

function isJsonInt(v: Json | undefined, kind: keyof typeof JSON_INT_RANGES): boolean {
  const [lo, hi] = JSON_INT_RANGES[kind];
  return typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;
}

function isFiniteJsonNumber(v: Json | undefined): boolean {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * A list kept for RPE (`ignoredLists`) must still parse with Phira's event struct (F§6.1): a list of objects with
 * startTime / endTime [i32, u32, u32 > 0], start / end of the list's value type, and integer / float typing in
 * the optional easing fields. Unknown keys are free.
 */
function checkIgnoredList(key: string, value: Json, path: string): void {
  if (!Array.isArray(value)) fail(path, 'a list kept for RPE must be a JSON list of events');
  (value as readonly Json[]).forEach((item, i) => {
    const p = `${path}[${String(i)}]`;
    if (item === null || typeof item !== 'object' || Array.isArray(item)) fail(p, 'an event must be a JSON object');
    const o = item as { readonly [k: string]: Json };
    const get = (k: string): Json | undefined => (Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined);
    for (const t of ['startTime', 'endTime'] as const) {
      const v = get(t);
      const ok =
        Array.isArray(v) &&
        v.length === 3 &&
        isJsonInt((v as readonly Json[])[0], 'i32') &&
        isJsonInt((v as readonly Json[])[1], 'u32') &&
        isJsonInt((v as readonly Json[])[2], 'u32') &&
        (v as readonly Json[])[2] !== 0;
      if (!ok) fail(`${p}.${t}`, 'a beat must be [i32, u32, u32] with a non-zero last number');
    }
    for (const k of ['start', 'end'] as const) {
      const v = get(k);
      if (key === 'textEvents' ? typeof v !== 'string' : !isFiniteJsonNumber(v)) {
        fail(`${p}.${k}`, key === 'textEvents' ? 'a text event value must be a string' : 'must be a finite number');
      }
    }
    const ints = [
      ['easingType', 'i32'],
      ['bezier', 'u8'],
      ['linkgroup', 'i32'],
    ] as const;
    for (const [k, kind] of ints) {
      const v = get(k);
      if (v !== undefined && !isJsonInt(v, kind)) fail(`${p}.${k}`, `must be an integer of type ${kind}`);
    }
    for (const k of ['easingLeft', 'easingRight'] as const) {
      const v = get(k);
      if (v !== undefined && !isFiniteJsonNumber(v)) fail(`${p}.${k}`, 'must be a finite number');
    }
    const bp = get('bezierPoints');
    if (
      bp !== undefined &&
      !(Array.isArray(bp) && bp.length === 4 && (bp as readonly Json[]).every((x) => isFiniteJsonNumber(x)))
    ) {
      fail(`${p}.bezierPoints`, 'must be four finite numbers');
    }
  });
}

function writeExtended(w: JsonWriter, ext: IrExtended, line: IrLine, path: string): void {
  for (const [key, value] of ext.ignoredLists) {
    const allowed =
      (key === 'gifEvents' && line.textureKind === 'builtin') ||
      ((key === 'textEvents' || key === 'paintEvents') && line.textureKind !== 'builtin');
    if (!allowed) fail(`${path}.${key}`, 'only lists Phira ignores for this line kind can be kept verbatim');
    if (ext[key] !== undefined) fail(`${path}.${key}`, 'a list cannot be both modelled and kept verbatim');
    checkIgnoredList(key, value, `${path}.${key}`);
  }
  const fields: Record<string, FieldValue> = {};
  for (const key of EXTENDED_LIST_KEYS) {
    const list = ext[key];
    const ignored = ext.ignoredLists.find(([k]) => k === key);
    if (list !== undefined) {
      if (key === 'gifEvents' && line.textureKind !== 'gif') {
        fail(`${path}.gifEvents`, 'gifEvents on a texture that is not a GIF make Phira fail to load the chart');
      }
      fields[key] = () => {
        writeEventList(w, list, key, `${path}.${key}`);
      };
    } else if (ignored !== undefined) {
      fields[key] = () => {
        w.json(ignored[1]);
      };
    } else if (ext.nullKeys.includes(key)) {
      fields[key] = () => {
        w.null();
      };
    }
  }
  writeObject(w, 'extended', path, fields, ext.extra);
}

function writeNote(w: JsonWriter, n: IrNote, path: string): void {
  if (![1, 2, 3, 4].includes(n.type)) fail(`${path}.type`, 'note type must be 1-4');
  writeObject(
    w,
    'note',
    path,
    {
      above: n.above,
      alpha: n.alpha,
      endTime: n.endTime,
      hitsound: n.hitsound,
      isFake: n.isFake,
      judgeArea: n.judgeArea,
      positionX: n.positionX,
      size: n.size,
      speed: n.speed,
      startTime: n.startTime,
      tint: n.tint,
      tintHitEffects: n.tintHitEffects,
      type: n.type,
      visibleTime: n.visibleTime,
      yOffset: n.yOffset,
    },
    n.extra,
  );
}

/**
 * A control curve: per point `x`, `easing` and the curve's value key in schema order, typed by the schema, then
 * the extra keys as f32 (Phira flattens them into a HashMap<String, f32>; another curve's value key, such as `pos`
 * on an alpha curve, is an extra key here). An extra key equal to a written key never overrides it.
 */
function writeControls(w: JsonWriter, points: readonly IrControlPoint[], valueKey: string, path: string): void {
  if (!isKnownKey('control', valueKey)) fail(path, `${valueKey} is not a control value key`);
  const keys = knownKeys('control').filter((k) => k === 'x' || k === 'easing' || k === valueKey);
  w.beginArray();
  points.forEach((p, i) => {
    const pp = `${path}[${String(i)}]`;
    w.beginObject();
    for (const k of keys) {
      w.key(k);
      writeField(w, 'control', k, k === 'x' ? p.x : k === 'easing' ? p.easing : p.value, pp);
    }
    const seen = new Set<string>(keys);
    for (const [k, v] of p.extra) {
      if (seen.has(k)) continue;
      seen.add(k);
      w.key(k);
      w.f32(v);
    }
    w.endObject();
  });
  w.endArray();
}

/** A judge line; `own` = only the line's own fields (no eventLayers / extended / notes: the 'line' fragment). */
function writeLine(w: JsonWriter, line: IrLine, path: string, own: boolean): void {
  if (line.father < -1) fail(`${path}.father`, 'father must be -1 or a line index');
  const sub = (key: string): string => (path === '' ? key : `${path}.${key}`);
  const controls = (key: ControlCurveKey): FieldValue => {
    const points = line[key];
    return points === undefined
      ? undefined
      : () => {
          writeControls(w, points, CONTROL_CURVES[key], sub(key));
        };
  };
  const ext = line.extended;
  writeObject(
    w,
    'line',
    path,
    {
      Group: line.Group,
      Name: line.Name,
      Texture: line.Texture,
      alphaControl: controls('alphaControl'),
      anchor: () => {
        w.beginArray();
        w.f64(line.anchor[0]);
        w.f64(line.anchor[1]);
        w.endArray();
      },
      attachUI: line.attachUI ?? undefined,
      bpmfactor: line.bpmfactor,
      eventLayers: own
        ? undefined
        : () => {
            w.beginArray();
            line.eventLayers.forEach((layer, i) => {
              if (layer === null) w.null();
              else writeLayer(w, layer, `${sub('eventLayers')}[${String(i)}]`);
            });
            w.endArray();
          },
      extended:
        own || ext === 'absent'
          ? undefined
          : () => {
              if (ext === null) w.null();
              else writeExtended(w, ext, line, sub('extended'));
            },
      father: line.father,
      isCover: line.isCover,
      isGif: line.isGif,
      notes:
        !own && (line.notesKey === 'present' || line.notes.length > 0)
          ? () => {
              w.beginArray();
              line.notes.forEach((n, i) => {
                writeNote(w, n, `${sub('notes')}[${String(i)}]`);
              });
              w.endArray();
            }
          : undefined,
      numOfNotes: line.numOfNotes,
      posControl: controls('posControl'),
      rotateWithFather: line.rotateWithFather,
      sizeControl: controls('sizeControl'),
      skewControl:
        line.skewControl === undefined
          ? undefined
          : () => {
              w.json(line.skewControl ?? null);
            },
      yControl: controls('yControl'),
      zOrder: line.zOrder,
    },
    line.extra,
  );
}

function writeRpeVersion(w: TextJsonWriter, v: IrMeta['RPEVersion']): void {
  if (typeof v === 'number') {
    w.int(v, 'i32');
    return;
  }
  if (v.kind === 'float') {
    w.rawNumber(v.lexeme);
    return;
  }
  const parsed = parseJsonLossless(v.lexeme);
  if (!parsed.ok || parsed.node.k !== 'str' || parsed.leading.bom || parsed.leading.whitespace) {
    fail('META.RPEVersion', `${v.lexeme} is not a JSON string token`);
  }
  // Verbatim: L1 compares raw lexemes as text, so escapes must survive (`"\u0031\u0037\u0030"` stays as is).
  w.rawString(v.lexeme);
}

function writeMeta(w: TextJsonWriter, m: IrMeta): void {
  writeObject(
    w,
    'meta',
    'META',
    {
      RPEVersion: () => {
        writeRpeVersion(w, m.RPEVersion);
      },
      background: m.background,
      charter: m.charter,
      composer: m.composer,
      id: m.id,
      illustration: m.illustration,
      level: m.level,
      name: m.name,
      offset: m.offset,
      song: m.song,
    },
    m.extra,
  );
}

function writeBpmList(w: JsonWriter, list: readonly IrBpm[]): void {
  if (list.length === 0) fail('BPMList', 'the BPM list must not be empty (Phira panics)');
  w.beginArray();
  list.forEach((b, i) => {
    const path = `BPMList[${String(i)}]`;
    if (!(b.bpm > 0)) fail(`${path}.bpm`, 'BPM must be positive');
    writeObject(w, 'bpm', path, { bpm: b.bpm, startTime: b.startTime }, b.extra);
  });
  w.endArray();
}

function writeChart(w: TextJsonWriter, ir: IrChart): void {
  const tags = ir.timeTags;
  writeObject(
    w,
    'chart',
    '',
    {
      BPMList: () => {
        writeBpmList(w, ir.BPMList);
      },
      META: () => {
        writeMeta(w, ir.META);
      },
      judgeLineGroup:
        ir.judgeLineGroup.length === 0
          ? undefined
          : () => {
              w.beginArray();
              for (const g of ir.judgeLineGroup) w.string(g);
              w.endArray();
            },
      judgeLineList: () => {
        w.beginArray();
        ir.judgeLineList.forEach((line, i) => {
          const path = `judgeLineList[${String(i)}]`;
          if (line.father >= ir.judgeLineList.length) fail(`${path}.father`, 'father is not a line index');
          writeLine(w, line, path, false);
        });
        w.endArray();
      },
      chartTime: ir.chartTime,
      multiLineString: ir.multiLineString,
      multiScale: ir.multiScale,
      timeTags:
        tags === undefined
          ? undefined
          : () => {
              w.beginArray();
              tags.forEach((tag, i) => {
                w.beginObject();
                w.key('name');
                w.string(tag.name);
                w.key('time');
                writeTriple(w, tag.time, `timeTags[${String(i)}].time`);
                w.endObject();
              });
              w.endArray();
            },
      xybind: ir.xybind,
    },
    ir.rootExtra,
  );
}

/** The chart JSON text (same output as encodeRpe, as a string). */
export function encodeRpeText(ir: IrChart, style: EncodeStyle): string {
  const w = createTextJsonWriter({ pretty: style.pretty, floatStyle: style.floatStyle });
  writeChart(w, ir);
  const text = w.finishText();
  if (!text.startsWith('{') || !text.includes('"META"')) fail('', 'the chart must start with "{" and contain "META"');
  return text;
}

export const encodeRpe: EncodeRpeFn = (ir, style) => new TextEncoder().encode(encodeRpeText(ir, style));

export const encodeRpeFragment: EncodeRpeFragmentFn = (frag: RpeFragmentIr, style) => {
  const w = createTextJsonWriter({ pretty: style.pretty, floatStyle: style.floatStyle });
  switch (frag.kind) {
    case 'events': {
      const list = frag.list ?? null;
      if (list !== null && !isEventListKey(list)) fail('', `${list} is not an RPE event list`);
      writeEventList(w, frag.ir, list, '');
      break;
    }
    case 'notes':
      w.beginArray();
      frag.ir.forEach((n, i) => {
        writeNote(w, n, `[${String(i)}]`);
      });
      w.endArray();
      break;
    case 'line':
      writeLine(w, frag.ir, '', true);
      break;
    case 'controls':
      writeControls(w, frag.ir, frag.controlKey, '');
      break;
  }
  return w.finishText();
};
